
import {Inject, Prop, Vue, Component, Watch} from 'vue-property-decorator';
import {} from 'googlemaps';
import {MAFUser, PositionObfuscator} from '@/store/User/types';
import {mapDashboard, MarkerInjectable} from '@/components/common/types';
import GeoPoint = firebase.firestore.GeoPoint;
import UserInfoWindow from '@/components/common/UserInfoWindow.vue';
import firebase from 'firebase/app';
import {Action, Getter, Mutation, State} from 'vuex-class';
import {LookupIcon} from '@/store/types';
import { UserWithMessage } from '../../store/Friends/types';

/* tslint:disable:no-unused-expression */
/* tslint:disable:max-line-length */

const infoWindow = Vue.extend(UserInfoWindow);
const namespace = 'userState';
const friendNamespace = 'friendState';
@Component({
    components: {},
})
export default class UserMarker extends Vue {
    @Prop() public hasInfoWindow!: boolean;
    @Prop() public readonly selectedFriend!: string;
    @Prop(MAFUser) public readonly user!: MAFUser;
    @Prop() public readonly obfuscate!: boolean;
    @Prop() public readonly position!: GeoPoint | undefined;
    @State('self', {namespace}) public self!: MAFUser;
    @Getter('getFunStuff', {namespace}) public getFunStuff!: LookupIcon[];
    @Getter('getFriendsAndMessages', {namespace: friendNamespace}) public getFriendMessages!: UserWithMessage[];
    @Mutation('setCurrentlyChattingWith', {namespace: friendNamespace}) public setCurrentlyChattingWith!: any;
    @Inject('gMap') public gMap!: any;
    public map: google.maps.Map | null;
    public marker: google.maps.Marker | null;
    public googleMap: typeof google | null;
    public infoWindow: google.maps.InfoWindow | null;
    public BASE_URL: string;

    constructor() {
        super();
        this.BASE_URL = process.env.BASE_URL ? process.env.BASE_URL : '';
        this.map = null;
        this.marker = null;
        this.googleMap = null;
        this.infoWindow = null;
    }

    public created() {
        const self = this;
        self.gMap((maps: MarkerInjectable) => {
            self.map = maps.map;
            self.googleMap = maps.googleMap;
            if (this.position) {
                this.clearMarker();
                self.marker = self.createMarker();
                this.addInfoWindow();

                if (this.selectedFriend && this.selectedFriend === this.user.uid) {
                    setTimeout(() => {
                        self.selectFriend();
                    }, 3000);
                }
            }
        });
    }

    get friendMessages(): UserWithMessage[] {
        return this.getFriendMessages;
    }

    get compPos() {
        if (this.position && this.map) {
            if (this.obfuscate) {
                // 402 meters is .25 miles or a total of half a mile
                return PositionObfuscator.generateRandomPointsFromGeopoint(this.position, 402);
            }
            return this.position;
        }
        return null;
    }

    @Watch('compPos')
    public createMarkerOnPosition() {
        if (this.compPos) {
            this.clearMarker();
            this.marker = this.createMarker();
            this.addInfoWindow();
        }
    }

    @Watch('selectedFriend')
    public selectFriend() {
        if (this.selectedFriend === this.user.uid && this.self.isAdmin) {
            if (this.infoWindow && this.map && this.marker) {
                this.infoWindow.open(this.map, this.marker);
            }
        } else if (this.infoWindow) {
            this.infoWindow.close();
        }
    }

    public openChatWindow(user: MAFUser) {
        this.$router.push('/dashboard/messages');
        this.setCurrentlyChattingWith(user);
    }

    public async addInfoWindow() {
        if (this.map && this.googleMap && this.marker && this.hasInfoWindow && !this.self.isAdmin && this.self.userType !== 'Organization') {
            // admin cannot open info windows (so all map markers can load in)
            // Organizations cannot open info windows (they don't personally accept terms and agreements and shouldn't have access to users)

            const self = this;

            this.infoWindow = new google.maps.InfoWindow({
                pixelOffset: new this.googleMap.maps.Size(150, 208, 'px', 'px'),
            });
            const interestIcons = await this.getFunStuff;
            const iw = new infoWindow({
                propsData: {
                    self: this.self,
                    user: this.user,
                    infoWindow: this.infoWindow,
                    parent: this,
                    funStuff: interestIcons,
                },
            });
            iw.$mount();
            this.infoWindow.setContent(iw.$el);
            this.marker.addListener('click', () => {
                if (self.infoWindow && self.map && self.marker) {
                    self.infoWindow.open(self.map, self.marker);
                }
            });
        }
    }

    public createMarker(): google.maps.Marker | null {
        const icon = this.createIcon();

        if (this.googleMap && icon && this.compPos && this.map) {
            return new this.googleMap.maps.Marker({
                position: {lat: +this.compPos.latitude, lng: +this.compPos.longitude},
                draggable: false,
                map: this.map,
                optimized: false,
                icon,
            });
        }
        return null;

    }

    public createIcon(): google.maps.Icon | null {
        let marker = '';
        // let marker = 'orange-dot';
        // const friendIdx = this.friendMessages.findIndex((f) => f.user.uid === this.user.uid);

        if (this.self.uid === this.user.uid) {
            marker = 'red-dot';
        } else if (this.user.isAdmin) {
            marker = 'yellow-dot';
        } else if (this.user.userType === 'My Parent' || this.user.userType === 'A Caregiver' || this.user.userType === 'Parent/Caregiver') {
            marker = 'purple-dot';
        } else if (this.user.userType === 'General User' || this.user.userType === 'Me' || this.user.userType === 'Me and Parents' || this.user.userType === 'Me and Caregiver') {
            marker = 'ltblue-dot';
        } else if (this.user.userType === 'Organization') {
            marker = 'orange-dot';
        }

        if (this.googleMap) {
            const icon = {
                url: `${this.BASE_URL}img/mapmarkers/${marker}.png`,
                size: new this.googleMap.maps.Size(35, 35, 'px', 'px'),
                scaledSize: new this.googleMap.maps.Size(35, 35, 'px', 'px'),
            };

            return icon;
        }
        return null;
    }

    public render() {
        return null;
    }

    public clearMarker(): void {
        if (this.marker) {
            this.marker ? this.marker.setMap(null) : this.marker;
            this.marker = null;
            delete this.marker;
        }
    }

    public beforeDestroy() {
        this.clearMarker();
    }
}
